<template>
  <div class="page-container animate-fade-in-up">
    <PageHeader icon="el-icon-s-tools" title="评估规则配置" subtitle="集中管理评分规则、评估说明等系统配置" />

    <!-- ===== 上区：风险等级 + 评分规则说明 ===== -->
    <div class="top-section">
      <!-- 左侧：风险阈值配置 -->
      <div class="card risk-card">
        <div class="section-title">风险等级预览</div>
        <div class="risk-preview">
          <div class="risk-row">
            <span class="risk-dot" style="background: var(--color-low);"></span>
            <span class="risk-label">低风险：</span>
            <span class="risk-range">0-{{ scoringForm.lowMax }}分</span>
          </div>
          <div class="risk-row">
            <span class="risk-dot" style="background: var(--color-medium);"></span>
            <span class="risk-label">中风险：</span>
            <span class="risk-range">{{ scoringForm.lowMax + 1 }}-{{ scoringForm.mediumMax }}分</span>
          </div>
          <div class="risk-row">
            <span class="risk-dot" style="background: var(--color-high);"></span>
            <span class="risk-label">高风险：</span>
            <span class="risk-range">&ge;{{ scoringForm.mediumMax + 1 }}分</span>
          </div>
        </div>

        <div class="threshold-inputs">
          <div class="input-item">
            <div class="input-label">低风险上限</div>
            <el-input-number v-model="scoringForm.lowMax" :min="0" :max="20" />
          </div>
          <div class="input-item">
            <div class="input-label">中风险上限</div>
            <el-input-number v-model="scoringForm.mediumMax" :min="1" :max="30" />
          </div>
        </div>

        <div v-if="$hasPermission('scoring:edit')" class="section-actions">
          <el-button type="primary" class="btn-click" @click="saveScoring">
            <i class="el-icon-check"></i> 保存配置
          </el-button>
          <el-button class="btn-click" @click="resetScoring">重置为默认</el-button>
        </div>
      </div>

      <!-- 右侧：评分规则说明 -->
      <div class="card rules-card">
        <div class="section-title">评分规则说明</div>
        <div class="section-subtitle">前台评估页右侧分数面板展示的评分规则文本</div>
        <SimpleRichEditor v-model="scoringForm.content" />
        <div class="setting-hint">
          <i class="el-icon-info"></i> 保存后，前台评估页面将自动显示最新的评分规则
        </div>
        <div v-if="$hasPermission('scoring:edit')" class="section-actions right-actions">
          <el-button type="primary" class="btn-click" @click="saveScoring">
            <i class="el-icon-check"></i> 保存规则
          </el-button>
        </div>
      </div>
    </div>

    <!-- ===== 下区：评估说明 ===== -->
    <div class="card instruction-card-full">
      <div class="section-header-row">
        <div>
          <div class="section-title">评估说明</div>
          <div class="section-subtitle">前台评估页顶部展示的评估说明文本</div>
        </div>
        <div class="preview-badge" @click="openInstructionPreview">
          <i class="el-icon-view"></i>
          前台预览
        </div>
      </div>
      <SimpleRichEditor v-model="instructionContent" />
      <div class="setting-hint">
        <i class="el-icon-info"></i> 保存后，前台评估页面将自动显示最新的评估说明
      </div>
      <div v-if="$hasPermission('scoring:edit')" class="section-actions">
        <el-button type="primary" class="btn-click" @click="saveInstruction">
          <i class="el-icon-check"></i> 保存说明
        </el-button>
      </div>
    </div>

    <!-- 前台预览弹窗 -->
    <el-dialog title="前台效果预览" :visible.sync="previewDialog.visible" width="560px" :append-to-body="true">
      <div class="preview-body">
        <div class="preview-hint">以下为该说明在前台评估页的展示效果：</div>
        <div class="preview-card">
          <div class="preview-card-title">
            <i class="el-icon-document" />
            评估说明
          </div>
          <pre class="preview-card-text">{{ previewDialog.content }}</pre>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="previewDialog.visible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '../../components/PageHeader.vue'
import SimpleRichEditor from '../../components/SimpleRichEditor.vue'

export default {
  name: 'ConfigCenter',
  components: { PageHeader, SimpleRichEditor },
  data() {
    return {
      scoringForm: {
        lowMax: 4,
        mediumMax: 10,
        content: ''
      },
      instructionContent: '',
      previewDialog: { visible: false, content: '' }
    }
  },
  computed: {
    activeInstruction() {
      return this.$store.getters.activeInstruction
    }
  },
  watch: {
    '$store.state.scoringRules.rules': {
      immediate: true,
      handler() {
        this.initScoringForm()
      }
    },
    activeInstruction: {
      immediate: true,
      handler(instr) {
        this.instructionContent = instr ? (instr.content || '') : ''
      }
    }
  },
  created() {
    this.initScoringForm()
    this.loadInstructions()
  },
  methods: {
    // ===== 评分规则 =====
    initScoringForm() {
      const s = this.$store.state.settings.settings
      this.scoringForm.lowMax = s.thresholds ? s.thresholds.lowMax : 4
      this.scoringForm.mediumMax = s.thresholds ? s.thresholds.mediumMax : 10
      const rules = this.$store.state.scoringRules.rules
      this.scoringForm.content = rules && rules.content ? rules.content : ''
    },
    async saveScoring() {
      const taskId = this.$store.getters.currentTaskId
      try {
        await this.$api.updateSetting({
          task_id: taskId || null,
          config_key: 'settings',
          config_value: {
            thresholds: { lowMax: this.scoringForm.lowMax, mediumMax: this.scoringForm.mediumMax },
            labels: { low: '低风险', medium: '中风险', high: '高风险' },
            system: { name: 'AI骨干人才保留助手', version: 'v3.1' }
          }
        })
        await this.$api.updateSetting({
          task_id: taskId || null,
          config_key: 'scoring_rules',
          config_value: {
            content: this.scoringForm.content,
            updated_at: new Date().toISOString().replace('T', ' ').slice(0, 19)
          }
        })

        const s = {
          ...this.$store.state.settings.settings,
          thresholds: { lowMax: this.scoringForm.lowMax, mediumMax: this.scoringForm.mediumMax }
        }
        const rules = {
          rule_id: 1,
          content: this.scoringForm.content,
          updated_at: new Date().toISOString().replace('T', ' ').slice(0, 19)
        }
        if (taskId) {
          this.$store.commit('SET_SETTINGS_FOR_TASK', { taskId, settings: s })
          this.$store.commit('SET_RULES_FOR_TASK', { taskId, rules })
        } else {
          this.$store.commit('SET_SETTINGS', s)
          this.$store.commit('SET_SCORING_RULES', rules)
        }
        this.$message.success('配置已保存，前台将实时生效')
      } catch (e) {
        this.$message.error('保存失败：' + ((e.response && e.response.data && e.response.data.message) || e.message || '未知错误'))
      }
    },
    resetScoring() {
      this.$confirm('确定重置为默认配置？', '提示').then(() => {
        const taskId = this.$store.getters.currentTaskId
        const defaultSettings = {
          thresholds: { lowMax: 4, mediumMax: 10 },
          labels: { low: '低风险', medium: '中风险', high: '高风险' },
          system: { name: 'AI骨干人才保留助手', version: 'v3.1' }
        }
        const defaultRules = {
          rule_id: 1,
          content: '加分项表明员工存在离职倾向，勾选"是"即累加对应分值。\n\n0-4分：低风险\n5-10分：中风险\n≥11分：高风险',
          updated_at: new Date().toISOString().replace('T', ' ').slice(0, 19)
        }
        if (taskId) {
          this.$store.commit('SET_SETTINGS_FOR_TASK', { taskId, settings: defaultSettings })
          this.$store.commit('SET_RULES_FOR_TASK', { taskId, rules: defaultRules })
        } else {
          this.$store.commit('SET_SETTINGS', defaultSettings)
          this.$store.commit('SET_SCORING_RULES', defaultRules)
        }
        this.$message.success('已重置')
        this.initScoringForm()
      }).catch(() => {})
    },

    // ===== 评估说明 =====
    async loadInstructions() {
      try {
        const taskId = this.$store.getters.currentTaskId
        const res = await this.$api.fetchInstructions(taskId ? { task_id: taskId } : {})
        if (res.data) {
          this.$store.commit('SET_INSTRUCTIONS', res.data)
        }
      } catch (e) {
        console.warn('[ConfigCenter] 加载评估说明失败:', e.message)
      }
    },
    async saveInstruction() {
      if (!this.instructionContent.trim()) {
        this.$message.error('说明内容不能为空')
        return
      }
      const taskId = this.$store.getters.currentTaskId
      try {
        if (this.activeInstruction && this.activeInstruction.instruction_id) {
          await this.$api.updateInstruction(this.activeInstruction.instruction_id, {
            content: this.instructionContent.trim(),
            status: 1
          })
        } else {
          await this.$api.createInstruction({
            task_id: taskId || null,
            content: this.instructionContent.trim(),
            status: 1,
            is_active: 1
          })
        }
        this.$message.success('评估说明已保存，前台将实时生效')
        await this.loadInstructions()
      } catch (e) {
        this.$message.error('保存失败：' + ((e.response && e.response.data && e.response.data.message) || e.message || '未知错误'))
      }
    },
    openInstructionPreview() {
      this.previewDialog.content = this.instructionContent
      this.previewDialog.visible = true
    }
  }
}
</script>

<style scoped>
/* ===== 上区：双栏布局 ===== */
.top-section {
  display: flex;
  gap: var(--space-2xl);
  margin-bottom: var(--space-2xl);
}

/* 通用卡片 */
.card {
  padding: var(--space-xl);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
}

.risk-card {
  flex: 0 0 340px;
}
.rules-card {
  flex: 1 1 auto;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: var(--space-lg);
}
.section-subtitle {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-md);
}

/* 风险等级预览 */
.risk-preview {
  padding: var(--space-lg);
  background: var(--color-bg);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-xl);
}
.risk-row {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  font-size: 14px;
  margin-bottom: var(--space-sm);
}
.risk-row:last-child { margin-bottom: 0; }
.risk-dot {
  width: 10px;
  height: 10px;
  border-radius: var(--radius-full);
  flex-shrink: 0;
}
.risk-label { font-weight: 600; color: var(--color-text); }
.risk-range { color: var(--color-text-secondary); }

/* 阈值输入 */
.threshold-inputs {
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
  margin-bottom: var(--space-xl);
}
.input-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-sm);
}

/* 操作按钮 */
.section-actions {
  display: flex;
  gap: var(--space-md);
  padding-top: var(--space-lg);
  border-top: 1px solid var(--color-border-light);
}
.right-actions {
  border-top: none;
  padding-top: var(--space-md);
  margin-top: var(--space-md);
}

/* 提示文字 */
.setting-hint {
  margin-top: 8px;
  font-size: 12px;
  color: var(--color-text-muted);
}

/* ===== 下区：评估说明 ===== */
.instruction-card-full {
  padding: var(--space-xl);
}
.section-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: var(--space-md);
}
.preview-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  padding: 6px 14px;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: 13px;
  color: var(--color-primary);
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}
.preview-badge:hover {
  background: var(--color-primary);
  color: white;
  border-color: var(--color-primary);
}

/* 前台预览 */
.preview-body { padding: 0 10px; }
.preview-hint {
  font-size: 13px;
  color: var(--color-text-muted);
  margin-bottom: 16px;
}
.preview-card {
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: var(--radius-lg);
  padding: 20px;
}
.preview-card-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.preview-card-text {
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  font-family: inherit;
}
</style>
